import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';
import { idToDate } from '@trello/dates';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { Analytics } from '@trello/atlassian-analytics';
import { usesEnglish } from '@trello/locale';
import { IconName, IconColor } from 'app/common/components/controls';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { IconButton } from 'app/common/components/controls/button/button';
import RouterLink from 'app/common/components/singlespa/router-link';
import { logError } from 'app/scripts/debug/log-error';
import { Team, Feature } from 'app/scripts/debug/constants';
import {
  useTeamTemplatePickerQuery,
  useDismissTeamTemplatePickerMutation,
} from '@trello/graphql/generated';
import { TemplateTile } from './TemplateTile';
import styles from './TeamTemplatePicker.less';

export enum TeamTypes {
  Business = 'business',
  Engineering = 'engineering',
  Design = 'design',
  Productivity = 'productivity',
  Education = 'education',
  Personal = 'personal',
  TeamManagement = 'team management',
  ProductManagement = 'product management',
  Marketing = 'marketing',
  Sales = 'sales',
  OperationsAndHR = 'operations and hr',
  Support = 'support',
  Other = 'other',
}

const teamTypeToTemplatesHash: Record<TeamTypes, string[]> = {
  [TeamTypes.Business]: [
    '5c3e2fdb0fa92e43b849d838',
    '54c93f6f836da7c4865460d2',
    '5db32c53e3c9dd6d5e710ecc',
  ],
  [TeamTypes.Engineering]: [
    '5b1a7ddba4b022243dce528e',
    '55107c70c088e6dc80bf2553',
    '5d823c15627de003f2e6025a',
  ],
  [TeamTypes.Design]: [
    '57a890c6504676888e1dd736',
    '5d66addc0d35125a62bc8b90',
    '58138a00e9178943a2fc3f19',
  ],
  [TeamTypes.Productivity]: [
    '5cffc8bc4899412c91f865e1',
    '5570bac5d9ebdefcf6a1080b',
    '5d84fa5309be465c4dc36087',
  ],
  [TeamTypes.Education]: [
    '5673286460eb65ac8b4a8c7d',
    '5d9389e457df5203e183a38e',
    '55d39827b8629b45cb9c722b',
  ],
  [TeamTypes.Personal]: [
    '566f021b04239fa9071235e3',
    '55ae67592f8829a37c1401ca',
    '5dae5379616fd0851a223f73',
  ],
  [TeamTypes.TeamManagement]: [
    '5d811df7f6a0ab8cb7412604',
    '56718f9d250f10c81e29cc8f',
    '542b0bd40d309dc6eba7ec89',
  ],
  [TeamTypes.ProductManagement]: [
    '5da0a80f975d5d836b3bb728',
    '5c3e30722972c101885955fb',
    '5c3e2f4db4f88f385d26e2cb',
  ],
  [TeamTypes.Marketing]: [
    '57a248d3977a6e388bf6cb63',
    '5d84f294025ff738907054fd',
    '5d811d1c09ae2c88a7b16eec',
  ],
  [TeamTypes.Sales]: [
    '5d78bdcc1e7bc748daa18357',
    '5d84088ac8b22a0e688ae2e5',
    '5810cf5086de0afa22d3dbea',
  ],
  [TeamTypes.OperationsAndHR]: [
    '5d7fce05db47576609123445',
    '5d7aa0d1976e75735f23ce55',
    '5c341d7ff7ba105d24e80a01',
  ],
  [TeamTypes.Support]: [
    '5da0b4c975eaf61076dcd26f',
    '5db701d97db0e076672aa580',
    '5d9f5fe909d01a601165c5b1',
  ],
  [TeamTypes.Other]: [
    '5da752d93e61dd54dee60c09',
    '5da0c23332c6eb0562f8e587',
    '5c0e4c8c4f599933363bc3b2',
  ],
};

interface TeamTemplatePickerProps {
  orgId: string;
}

export const TeamTemplatePickerUnconnected: React.FunctionComponent<TeamTemplatePickerProps> = ({
  orgId,
}) => {
  const [isDismissing, setIsDismissing] = useState(false);
  const [dismissTeamTemplatePicker] = useDismissTeamTemplatePickerMutation();
  const { data, loading, refetch } = useTeamTemplatePickerQuery({
    variables: {
      orgId,
    },
  });

  const DISMISS_MESSAGE = `team-template-picker-${orgId}`;
  const memberId = data?.member?.id || ('' as string);
  const oneTimeMessagesDismissed = data?.member?.oneTimeMessagesDismissed || [];
  const idMemberCreator = data?.organization?.idMemberCreator;
  const boards = data?.organization?.boards || [];
  let teamType: TeamTypes = data?.organization?.teamType as TeamTypes;

  if (!teamType || !teamTypeToTemplatesHash[teamType]) {
    teamType = TeamTypes.Other;
  }

  /*
   * Audience Definition:
   * For existing teams with < 4 boards, show to all users on team
   * For new teams with < 4 boards, show only to team creator
   */
  let isInAudience = false;
  if (!loading) {
    const startDate = moment('2020-02-26', 'YYYY-MM-DD');
    const isExistingTeam =
      moment(idToDate(orgId)).isBefore(startDate) ||
      !!Cookies.get('team-template-picker.simulateExistingOrg');
    isInAudience = isExistingTeam || idMemberCreator === memberId;
  }

  const shouldRender =
    useFeatureFlag('btg.team-template-picker', false) &&
    usesEnglish() &&
    isInAudience &&
    !oneTimeMessagesDismissed.includes(DISMISS_MESSAGE) &&
    !loading;

  const onDismiss = async () => {
    trackUe({
      category: Noun.TEAM_TEMPLATE_PICKER,
      verb: Verb.DISMISSES,
      directObj: Noun.TEAM_TEMPLATE_PICKER,
    });

    Analytics.sendUIEvent({
      action: 'dismissed',
      actionSubject: 'teamTemplatePicker',
      actionSubjectId: 'dismissTemplateButton',
      source: 'teamScreen',
      containers: {
        organization: { id: orgId },
      },
    });

    try {
      await dismissTeamTemplatePicker({
        variables: { memberId, messageId: DISMISS_MESSAGE },
        optimisticResponse: {
          __typename: 'Mutation',
          addOneTimeMessagesDismissed: {
            id: memberId,
            oneTimeMessagesDismissed: oneTimeMessagesDismissed!.concat([
              DISMISS_MESSAGE,
            ]),
            __typename: 'Member',
          },
        },
      });
    } catch (err) {
      logError({
        error: err,
        tags: {
          ownershipArea: Team.BizTeam,
          feature: Feature.TeamTemplatePicker,
        },
      });
    }
  };

  useEffect(() => {
    if (shouldRender) {
      trackUe({
        category: Noun.TEAM_TEMPLATE_PICKER,
        verb: Verb.VIEWS,
        directObj: Noun.TEAM_TEMPLATE_PICKER,
      });

      Analytics.sendScreenEvent({
        name: 'teamTemplatePicker',
        containers: {
          organization: {
            id: orgId,
          },
        },
      });
    }
  }, [orgId, shouldRender]);

  if (!shouldRender) {
    return null;
  }

  if (boards.length > 3) {
    if (isDismissing) {
      return null;
    }

    setIsDismissing(true);
    dismissTeamTemplatePicker({
      variables: { memberId, messageId: DISMISS_MESSAGE },
      optimisticResponse: {
        __typename: 'Mutation',
        addOneTimeMessagesDismissed: {
          id: memberId,
          oneTimeMessagesDismissed: oneTimeMessagesDismissed!.concat([
            DISMISS_MESSAGE,
          ]),
          __typename: 'Member',
        },
      },
    });
    return null;
  }

  const usedTemplateIds = boards.map(({ idBoardSource }) => idBoardSource);
  const templateIds = teamTypeToTemplatesHash[teamType].filter(
    (boardId) => !usedTemplateIds.includes(boardId),
  );

  if (!templateIds.length) {
    return null;
  }

  return (
    <div className={styles.teamTemplatePicker}>
      <div className={styles.teamTemplatePickerHeader}>
        <div>
          <h2>
            Start with a&nbsp;
            <span className={styles.teamTemplatePickerTeamType}>
              {!teamType || teamType === TeamTypes.Other ? '' : teamType + ' '}
            </span>
            template.
          </h2>
          <p>
            Choose a popular template or{' '}
            <RouterLink target="_blank" href="/templates">
              browse more templates
            </RouterLink>{' '}
            from the Trello community.
          </p>
        </div>
        <IconButton
          onClick={onDismiss}
          className={styles.teamTemplatePickerDismiss}
          icon={IconName.Close}
          color={IconColor.Dark}
        />
      </div>
      <div className={styles.teamTemplatePickerTemplates}>
        {templateIds.map((templateId) => (
          <div
            key={templateId}
            className={styles.teamTemplatePickerTemplatesTemplate}
          >
            <TemplateTile
              boardId={templateId}
              organizationId={orgId}
              onTemplateCopied={refetch}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export const TeamTemplatePicker: React.FunctionComponent<TeamTemplatePickerProps> = (
  props,
) => (
  <ComponentWrapper>
    <TeamTemplatePickerUnconnected {...props} />
  </ComponentWrapper>
);
